/**
 * Admin Users Hook
 * Fetch and manage paginated user list with search and filters
 */

import { apiUrl } from '@config/api';
import { useAuth } from '@contexts/AuthContext';
import { useCallback, useEffect, useState } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface UserListItem {
  id: string;
  email: string;
  displayName: string;
  firstName: string;
  lastName: string;
  role: string;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  onboardingCompletedAt: string | null;
}

export interface UseAdminUsersParams {
  page: number;
  limit: number;
  search: string;
  role?: string;
  status?: string;
}

export interface UseAdminUsersResult {
  users: UserListItem[];
  total: number;
  pages: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useAdminUsers(params: UseAdminUsersParams): UseAdminUsersResult {
  const { authFetch } = useAuth();
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        page: String(params.page),
        limit: String(params.limit),
        search: params.search,
        ...(params.role && { role: params.role }),
        ...(params.status && { status: params.status }),
      });

      const response = await authFetch(apiUrl(`/api/admin/users?${queryParams}`));

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Unauthorized - admin access required');
        }
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      setUsers(data.users);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [authFetch, params.page, params.limit, params.search, params.role, params.status]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    total,
    pages,
    loading,
    error,
    refetch: fetchUsers,
  };
}
